import type { FetchResult } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import type { CardDetailsFragment } from '@graphql/fragments/cards/core.graphql';
import { UpdateCardDocument, type UpdateCardMutation } from '@graphql/mutations/cards/card.graphql';
import { type CardAttributeTypeFragment, useCardAttributes } from '@hooks/use-card-attributes';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { useMemo } from 'react';

type UseBlocksAttributesArg = {
  card: Maybe<CardDetailsFragment>;
  categoryAllowList: string[];
};

type UseBlocksAttributesReturn = {
  isAttributeBlocked: (attributeId?: string | null) => boolean;
  /** A boolean flag to indicate if all attributes that belong to the category allow list are blocked  */
  attributesAreBlocked: boolean;
  /** A boolean flag to indicate if all attributes that belong to the category allow list are blocked by the template */
  attributesAreBlockedByTemplate: boolean;
  /** Categorised attributes for all categories in the allow list */
  categorisedAttributes: Record<string, CardAttributeTypeFragment[]>;
  toggleBlockAttribute: (attributeId?: string) => Promise<FetchResult<UpdateCardMutation> | undefined>;
  toggleBlockAllAttributes: () => Promise<FetchResult<UpdateCardMutation> | undefined>;
};

export const useBlockAttributes = ({
  categoryAllowList,
  card,
}: UseBlocksAttributesArg): UseBlocksAttributesReturn => {
  const { cardAttributeTypes } = useCardAttributes();

  const [updateCard] = useMutation(UpdateCardDocument);

  const templateBlockedAttributeIds = useMemo(() => {
    const templateBlockedAttributes = card?.template?.blocked_attributes ?? [];
    return new Set([...templateBlockedAttributes]?.map((attribute) => attribute.id));
  }, [card?.template?.blocked_attributes]);

  const blockableAttributeIds = cardAttributeTypes
    .filter((attribute) => attribute.is_blockable)
    .map((attribute) => attribute.id);

  /**
   * A set of all blocked attribute ids of the card/template, it does not matter if the attribute
   * belongs to the category allow list or not
   */
  const allBlockedAttributeIds = useMemo(() => {
    const cardBlockedAttributes = card?.blocked_attributes ?? [];

    return new Set([
      ...[...cardBlockedAttributes].map((attribute) => attribute.id),
      ...templateBlockedAttributeIds,
    ]);
  }, [card?.blocked_attributes, templateBlockedAttributeIds]);

  const categorisedAttributes = useMemo(() => {
    const allowedAttributes = cardAttributeTypes
      .filter((attribute) => !!attribute.category)
      .filter((attribute) => categoryAllowList.includes(attribute.category ?? ''));

    const allowedCategories = allowedAttributes
      .sort((a, b) => (a.category_rank ?? 0) - (b.category_rank ?? 0))
      .map((attribute) => attribute.category)
      .filter((category): category is NonNullable<CardAttributeTypeFragment['category']> => !!category);

    const asObject: Record<string, CardAttributeTypeFragment[]> = {};

    for (const category of allowedCategories) {
      asObject[category] = allowedAttributes
        .filter((attribute) => attribute.category === category)
        .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
    }

    return asObject;
  }, [cardAttributeTypes, categoryAllowList]);

  const attributesAreBlocked = useMemo(() => {
    const allAttributeIds = Object.values(categorisedAttributes)
      .flatMap((attributes) => attributes.map((attribute) => attribute.id))
      .filter((id) => blockableAttributeIds.includes(id));
    return allAttributeIds.every((id) => allBlockedAttributeIds.has(id));
  }, [allBlockedAttributeIds, blockableAttributeIds, categorisedAttributes]);

  const attributesAreBlockedByTemplate = useMemo(() => {
    // Cards should not be able to unblock attributes if blocked by template
    const allAttributeIds = Object.values(categorisedAttributes).flatMap((attributes) =>
      attributes.map((attribute) => attribute.id),
    );
    return allAttributeIds.every((id) => templateBlockedAttributeIds.has(id));
  }, [templateBlockedAttributeIds, categorisedAttributes]);

  const updateMutation = async (blockedAttributes: Set<string>) => {
    if (!card?.id) {
      return;
    }
    return await updateCard({
      variables: {
        input: {
          id: card.id,
          blocked_attribute_ids: Array.from(blockedAttributes),
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateCard: {
          __typename: 'UpdateCardPayload',
          card: {
            ...card,
            blocked_attributes: Array.from(blockedAttributes).map((id) => ({ id })),
          },
        },
      },
    });
  };

  const toggleBlockAttribute = async (attributeId?: string) => {
    if (!attributeId) {
      return;
    }

    const newBlockedAttributeIds = new Set(allBlockedAttributeIds);
    if (allBlockedAttributeIds.has(attributeId)) {
      newBlockedAttributeIds.delete(attributeId);
    } else {
      newBlockedAttributeIds.add(attributeId);
    }

    return await updateMutation(newBlockedAttributeIds); // Update the mutation with the new set
  };

  const toggleBlockAllAttributes = async () => {
    if (!card) {
      return;
    }

    const attributeIdsInAllowList = Object.values(categorisedAttributes)
      .flatMap((category) => category.map(({ id }) => id))
      .filter((id) => blockableAttributeIds.includes(id));

    // If all attributes are currently blocked, then we need to unblock all
    // We still want to keep the attributes that are not in the allow list blocked
    if (attributesAreBlocked) {
      const blockedAttributeIdsExcludedAllowed = new Set(
        Array.from(allBlockedAttributeIds).filter((id) => !attributeIdsInAllowList.includes(id)),
      );
      return await updateMutation(blockedAttributeIdsExcludedAllowed);
    }

    // If not all attributes are blocked, then we need to block all including
    // the existing blocked attributes that are not in the allow list
    const newAllBlockedAttributeIds = new Set([...allBlockedAttributeIds, ...attributeIdsInAllowList]);
    return await updateMutation(newAllBlockedAttributeIds);
  };

  const isAttributeBlocked = (attributeId?: string | null) => {
    if (!attributeId) {
      return false; // don't block it
    }

    /* 
      Warning - hack to fix edge case:
      
      It's possible for a card to block the profile_picture attribute,
      and to later apply a template that provides a profile_picture.
      
      Currently, this blocked attribute doesn't get cleaned up, as it probably should.
      
      In the interim, and under such circumstances, don't block the profile_picture field
    */
    const attributeIsProfilePicture =
      cardAttributeTypes.find((item) => item.id === attributeId)?.name === 'profile_picture';
    const templateProvidesProfilePicture = Boolean(card?.template?.images?.profile_picture);
    if (attributeIsProfilePicture && templateProvidesProfilePicture) {
      return false;
    }

    return allBlockedAttributeIds.has(attributeId);
  };

  return {
    isAttributeBlocked,
    categorisedAttributes,
    attributesAreBlocked,
    attributesAreBlockedByTemplate,
    toggleBlockAttribute,
    toggleBlockAllAttributes,
  };
};
